import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { registerRestrictionsUpdater } from './authStore';

export interface RestrictionsState {
  // Restriction flags
  restrictBrowseAll: boolean;
  restrictGridDisplay: boolean;
  restrictGridResultCount: number | boolean;
  restrictGraphDisplay: boolean;
  restrictSources: string | boolean;
  restrictChemical: boolean;
  restrictPathwayDisplay: boolean;
  restrictSort: boolean;
  restrictSortOrder: boolean;

  // Actions
  updateRestrictions: (isAuthenticated: boolean, groups: string[]) => void;
  setRestrictBrowseAll: (value: boolean) => void;
  setRestrictGridDisplay: (value: boolean) => void;
  setRestrictGridResultCount: (value: number | boolean) => void;
  setRestrictGraphDisplay: (value: boolean) => void;
  setRestrictSources: (value: string | boolean) => void;
  setRestrictChemical: (value: boolean) => void;
  setRestrictPathwayDisplay: (value: boolean) => void;
  setRestrictSort: (value: boolean) => void;
  setRestrictSortOrder: (value: boolean) => void;
  resetRestrictions: () => void;
}

// Default restrictions for unauthenticated users
const getDefaultRestrictions = () => ({
  restrictBrowseAll: true,
  restrictGridDisplay: true,
  restrictGridResultCount: 10,
  restrictGraphDisplay: true,
  restrictSources: false,
  restrictChemical: true,
  restrictPathwayDisplay: true,
  restrictSort: true,
  restrictSortOrder: true,
});

export const useRestrictionsStore = create<RestrictionsState>()(
  devtools(
    (set, get) => ({
      // Initial state - default restrictions
      ...getDefaultRestrictions(),

      // Individual setters
      setRestrictBrowseAll: restrictBrowseAll => set({ restrictBrowseAll }),
      setRestrictGridDisplay: restrictGridDisplay => set({ restrictGridDisplay }),
      setRestrictGridResultCount: restrictGridResultCount => set({ restrictGridResultCount }),
      setRestrictGraphDisplay: restrictGraphDisplay => set({ restrictGraphDisplay }),
      setRestrictSources: restrictSources => set({ restrictSources }),
      setRestrictChemical: restrictChemical => set({ restrictChemical }),
      setRestrictPathwayDisplay: restrictPathwayDisplay => set({ restrictPathwayDisplay }),
      setRestrictSort: restrictSort => set({ restrictSort }),
      setRestrictSortOrder: restrictSortOrder => set({ restrictSortOrder }),

      // Reset to default restrictions
      resetRestrictions: () => set(getDefaultRestrictions()),

      // Main update function based on authentication and groups
      updateRestrictions: (isAuthenticated: boolean, groups: string[]) => {
        const isAcademic = groups.includes('ROLE_ACADEMIC');
        const isTrial = groups.includes('ROLE_TRIAL');

        set({
          // Grid display - restricted for unauthenticated users
          restrictGridDisplay: !isAuthenticated,

          // Grid result count - different limits based on user type
          restrictGridResultCount:
            !isAuthenticated || !groups.length
              ? 10 // Unauthenticated or no groups
              : isTrial
                ? 30 // Trial users get 30 results
                : false, // Premium users get unlimited

          // Browse all - restricted for academic users
          restrictBrowseAll: isAcademic,

          // Analytics graphs - available to trial and premium users
          restrictGraphDisplay: !isAuthenticated || (isAcademic && !isTrial),

          // Sources - academic users only get curated sources
          restrictSources: isAcademic ? 'CURATED' : false,

          // Pathway display - restricted for unauthenticated and academic users
          restrictPathwayDisplay: !isAuthenticated || isAcademic,

          // Chemical data access - restricted for unauthenticated and academic users
          restrictChemical: !isAuthenticated || isAcademic,

          // Sorting - restricted for unauthenticated and academic users
          restrictSort: !isAuthenticated || isAcademic,

          // Sort order - restricted for unauthenticated and academic users
          restrictSortOrder: !isAuthenticated || isAcademic,
        });
      },
    }),
    {
      name: 'restrictions-store',
    },
  ),
);

// Register the restrictions updater with the auth store
// This creates the integration between the two stores
registerRestrictionsUpdater((isAuthenticated: boolean, groups: string[]) => {
  useRestrictionsStore.getState().updateRestrictions(isAuthenticated, groups);
});

// Computed selectors for common restriction checks
export const useRestrictionsSelectors = () => {
  const restrictions = useRestrictionsStore();

  return {
    // Check if user has any restrictions
    hasAnyRestrictions: Object.values(restrictions).some(value =>
      typeof value === 'boolean' ? value : value !== false,
    ),

    // Check if user can access premium features
    canAccessPremiumFeatures:
      !restrictions.restrictGridDisplay && restrictions.restrictGridResultCount === false,

    // Check if user has limited results
    hasLimitedResults: typeof restrictions.restrictGridResultCount === 'number',

    // Get result limit
    getResultLimit: () => {
      const { restrictGridResultCount } = restrictions;
      return typeof restrictGridResultCount === 'number' ? restrictGridResultCount : null;
    },

    // Check if user can access specific features
    canAccess: {
      browseAll: !restrictions.restrictBrowseAll,
      gridDisplay: !restrictions.restrictGridDisplay,
      unlimitedResults: restrictions.restrictGridResultCount === false,
      graphDisplay: !restrictions.restrictGraphDisplay,
      allSources: restrictions.restrictSources === false,
      chemicalData: !restrictions.restrictChemical,
      pathwayDisplay: !restrictions.restrictPathwayDisplay,
      sorting: !restrictions.restrictSort,
      sortOrder: !restrictions.restrictSortOrder,
    },

    // Get user type based on restrictions
    getUserTypeFromRestrictions: () => {
      if (restrictions.restrictGridDisplay) return 'unauthenticated';
      if (restrictions.restrictBrowseAll) return 'academic';
      if (restrictions.restrictGridResultCount === 30) return 'trial';
      if (restrictions.restrictGridResultCount === false) return 'premium';
      return 'unknown';
    },
  };
};
